<template>
  <main>
    <div v-if="loading" class="loading-container">
      <app-loading />
    </div>
    <div v-else class="job-details" role="tablist">
      <JobOverview :job="job?.data" />
      <div v-if="job?.data?.value?.closed_at">
        <div v-if="job?.data?.value?.successful_applicant_name" class="closed-container">
          <span class="closed-info"
            >Congratulations on selecting {{ job?.data?.value?.successful_applicant_name }} for this role! 🎉
          </span>
          <span class="closed-info">Now you can contact them through email or text.</span>
          <span class="closed-info">Make them an offer or start them on your internal candidate selection process</span>
          <div class="contact-details">
            <span class="contact-detail"
              ><Phone :color="PaletteColors['brand-primary']"></Phone>
              {{ job?.data?.value?.successful_applicant_phone }}</span
            >
            <span class="contact-detail"
              ><Envelope :color="PaletteColors['brand-primary']"></Envelope
              >{{ job?.data?.value?.successful_applicant_email }}</span
            >
          </div>
        </div>
        <div v-else class="closed-container">
          <span class="closed-info">You closed this job without selecting a candidate. </span>
          <span class="closed-info">Create a new job to continue hiring. </span>
          <span>
            <OpenPositions></OpenPositions>
          </span>
        </div>
      </div>
      <div class="accordions" v-else>
        <Accordion
          size="L"
          contentId="toggle-applicants"
          role="tab"
          :sticky-top="true"
          :initiallyOpened="tabOpen === TABS.APPLICANTS"
          :key="`applicants-${tabKey}`"
          accordion-group-id="jobs-accordion"
          label="Step 1 - Shortlist candidates"
          description="Choose who you want to proceed with"
          :ctaText="`${applicants.data.value ? applicants.data.value?.length : 0} applicant${
            applicants.data.value?.length !== 1 ? 's' : ''
          }`"
        >
          <template v-slot:content>
            <div v-if="!isApplicantsTabVisible" class="empty">
              <Info width="22" height="22" />
              Applicants who apply to this job will appear here
            </div>

            <div v-else class="applicants">
              <div class="applicant-card" v-for="employee in applicants.data.value" :key="employee.worker_id">
                <EmployeeCard
                  :claim="formatClaimForProfile(employee)"
                  :activelyLooking="employee?.properties?.talent_pool_onboarding_intent?.includes('employment')"
                />
                <div class="buttons">
                  <b-button
                    @click="onDenyClaim(employee.unfilled_shift_group_applicant_id)"
                    variant="outline-danger"
                    class="deny-button"
                    >Deny</b-button
                  >
                  <b-button
                    @click="onApproveClaim(employee.unfilled_shift_group_applicant_id)"
                    variant="outline-success"
                    class="approve-button"
                    >Shortlist</b-button
                  >
                </div>
              </div>
            </div>
          </template>
        </Accordion>

        <Accordion
          size="L"
          contentId="toggle-shortlisted"
          role="tab"
          :sticky-top="true"
          :initiallyOpened="tabOpen === TABS.SHORTLISTED"
          :key="`shortlisted-${tabKey}`"
          accordion-group-id="jobs-accordion"
          label="Step 2 - Post trial shifts or interviews"
          description="Candidates will select the ones they can attend"
          :ctaText="`${shortlistedCandidates ? shortlistedCandidates?.length : 0} shortlisted`"
        >
          <template v-slot:content>
            <div class="shortlisted">
              <div class="shortlisted-steps">
                <div class="group">
                  <div class="columns">
                    <div>
                      <div v-if="shortlistedCandidates.length === 0" class="empty">
                        <Info width="22" height="22" />Candidates you have shortlisted will appear here
                      </div>
                      <div class="list">
                        <EmployeeCardSmall
                          v-for="employee in shortlistedCandidates"
                          :key="employee.worker_id"
                          :claim="
                            formatClaimForProfile({
                              roles: employee.roles, // employee roles
                              function: employee.function, // shift function
                              role: employee.role, // shift role
                              raise_shifts_worked: employee.raise_shifts_worked,
                              raise_rating_avg: employee.raise_rating_avg,
                              internal_rating_for_worker: employee.internal_rating_for_worker,
                              worker_id: employee.worker_id,
                              worker_name: employee.worker_name,
                              distance_km: employee.distance_km,
                            })
                          "
                        />
                      </div>
                    </div>
                    <div class="list">
                      <ChildShiftCard v-for="shift in unclaimedShifts" :key="shift.shift_id" :shift="shift" />
                      <div class="create-shift-cta" v-b-modal="'add-interview-or-trial-shift-modal'">
                        <PlusCircleFilled width="36" height="36" :color="PaletteColors['brand-primary']" />
                        <div class="texts">
                          <span class="title">Interviews & trial shifts for this job</span>
                          <p class="description">Only your shortlisted candidates will be able to book these</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div></template
          >
        </Accordion>

        <Accordion
          size="L"
          contentId="toggle-assigned"
          role="tab"
          :sticky-top="true"
          :initiallyOpened="tabOpen === TABS.DECISION"
          :key="`decision-${tabKey}`"
          accordion-group-id="jobs-accordion"
          label="Step 3 - Make your decision"
          description="Offer the job to your preferred candidate"
          :ctaText="`${assignedShifts?.length ? assignedShifts.length : 0} assigned`"
        >
          <template v-slot:content>
            <div class="shortlisted">
              <div class="list">
                <AssignedShift
                  @onSelect="onSelectFinalCandidate(shift.winning_claim_user_id)"
                  :selected="selectedApplicantId === shift.winning_claim_user_id"
                  v-for="shift in assignedShifts"
                  :key="shift.shift_id"
                  :shift="shift"
                  :employee="getEmployeeForShift(shift)"
                />
              </div>
              <div v-if="assignedShifts?.length === 0" class="empty">
                <Info width="22" height="22" />
                Keep track of the candidates with booked or completed interviews or trial shifts, and make your
                decision!
              </div>
            </div>
          </template>

          <template v-slot:footer>
            <div class="hire-container">
              <div class="hire-container-left">
                <span class="hire-cost-span"> Cost of hire </span>
                <span class="hire-value-span">£350.00 + VAT</span>
              </div>
              <div v-if="selectedApplicant?.worker_name">
                <b-button
                  variant="primary"
                  v-b-modal="'select-candidate-modal'"
                  :disabled="!assignedShifts?.length || job?.data?.is_closed || !selectedApplicantId"
                  class="hire-button"
                >
                  <CheckCircle :color="PaletteColors['base-white']" width="16px" height="16px"> </CheckCircle>
                  Hire {{ selectedApplicant?.worker_name }}
                </b-button>
              </div>
              <div v-else>
                <b-button
                  variant="primary"
                  v-b-modal="'close-job-modal'"
                  :disabled="job?.data?.is_closed"
                  class="hire-button"
                >
                  <CheckCircle :color="PaletteColors['base-white']" width="16px" height="16px"> </CheckCircle>
                  Close job
                </b-button>
              </div>
            </div>
          </template>
        </Accordion>
      </div>
    </div>
    <add-interview-or-trial-shift-modal
      v-if="
        job.data.value?.location_id && job.data.value?.internal_job_role_id && job.data.value?.unfilled_shift_group_id
      "
      :jobId="job.data.value.unfilled_shift_group_id"
      :roleId="job.data.value.internal_job_role_id"
      :locationId="job.data.value.location_id"
    ></add-interview-or-trial-shift-modal>

    <Dialog
      modalId="select-candidate-modal"
      size="lg"
      :title="`Are you sure you want to select ${selectedApplicant?.worker_name} for this role?`"
      okBtnLabel="Select this candidate"
      @on-ok="onCloseJob"
    >
      <template #content>
        <div class="hire-content">
          <p>Time to contact your candidate and take the next step.</p>
          <p>You can make them an offer, or start this candidate in your official hiring process.</p>
          <p>
            Once you select a candidate, this job posting will be closed and no other candidates will be able to view
            it.
          </p>
        </div>
      </template>
    </Dialog>
    <Dialog
      modalId="close-job-modal"
      title="Are you sure you want to close this job without selecting a candidate?"
      okBtnLabel="Close without selection"
      okBtnVariant="danger"
      @on-ok="onCloseJob"
    />
  </main>
</template>

<script setup>
import OpenPositions from '@/pages/talent-pool/components/OpenPositions/index.vue';
import CheckCircle from '@/assets/icons/CheckCircle.vue';
import Info from '@/assets/icons/Info.vue';
import Envelope from '@/assets/icons/Envelope.vue';
import Phone from '@/assets/icons/Phone.vue';
import PlusCircleFilled from '@/assets/icons/PlusCircleFilled.vue';
import EmployeeCard from '@/pages/talent-pool/components/EmployeeCard.vue';
import EmployeeCardSmall from '@/pages/talent-pool/components/EmployeeCardSmall.vue';
import JobOverview from '@/pages/talent-pool/jobs/components/JobOverview.vue';
import { useRoute } from 'vue-router/composables';
import AppLoading from '@/components/AppLoading.vue';
import Accordion from '@/components/Accordion.vue';
import { useQuery, useMutation } from '@tanstack/vue-query';
import {
  getJobPosting,
  getApplicants,
  postClaimApproval,
  postClaimDenial,
  closeJobPosting,
} from '@/queries/talent-pool/jobs.js';
import { getShiftsForJob } from '@/queries/talent-pool/shifts.js';
import ChildShiftCard from '@/pages/talent-pool/jobs/components/ChildShiftCard.vue';
import AssignedShift from '@/pages/talent-pool/jobs/components/AssignedShift.vue';
import { computed, watch, ref } from 'vue';
import AddInterviewOrTrialShiftModal from '@/pages/talent-pool/jobs/components/AddInterviewOrTrialShiftModal.vue';
import { isUnclaimed, isFulfilled, isClaimed, isNoShow, isCompleted } from '@/pages/talent-pool/utils/ShiftStatus.js';
import { formatClaimForProfile } from '@/pages/talent-pool/utils/profile.js';
import { PaletteColors } from '@/Theme';
import Dialog from '@/components/Dialog.vue';

const TABS = {
  APPLICANTS: 'applicants',
  SHORTLISTED: 'shortlisted',
  DECISION: 'decision',
};

const route = useRoute();
const jobId = route.params.jobId;

const job = useQuery(getJobPosting(jobId));
const shortlisted = useQuery(getApplicants(jobId, { status: 'APPROVED' }));
const applicants = useQuery(getApplicants(jobId, { status: 'PENDING' }));
const shiftsForJob = useQuery(getShiftsForJob(jobId));
const approveClaim = useMutation(postClaimApproval());
const denyClaim = useMutation(postClaimDenial());
const closeJob = useMutation(closeJobPosting());

const loading = ref(true);
const tabOpen = ref(null);
const tabKey = ref(new Date().toISOString());

const selectedApplicantId = ref(null);

const selectedApplicant = computed(() => {
  return shortlisted?.data?.value?.find(candidate => candidate.worker_user_id === selectedApplicantId.value);
});

const getEmployeeForShift = shift => {
  return shortlisted?.data?.value?.find(employee => shift.winning_claim_user_id === employee.worker_user_id);
};

const onSelectFinalCandidate = applicant => {
  if (selectedApplicantId.value === applicant) {
    selectedApplicantId.value = null;
    return;
  }
  selectedApplicantId.value = applicant;
};

const onApproveClaim = async applicantId => {
  approveClaim.mutate(applicantId);
  // we don't have control over the internal state of the tab, so we have to force a rerender
  tabOpen.value = TABS.SHORTLISTED;
  tabKey.value = new Date().toISOString();
};

const onDenyClaim = async applicantId => {
  denyClaim.mutate(applicantId);
};

const onCloseJob = async () => {
  const payload = {
    jobId: jobId,
    payload: {
      successful_applicant_id: selectedApplicant.value?.unfilled_shift_group_applicant_id,
    },
  };
  closeJob.mutate(payload);
};

const isApplicantsTabVisible = computed(() => !!applicants.data.value?.length);
const shortlistedCandidates = computed(() => shortlisted.data?.value?.filter(employee => !employee.is_assigned) || []);

const unclaimedShifts = computed(
  () => shiftsForJob.data.value && shiftsForJob.data.value.filter(shift => isUnclaimed(shift) || isClaimed(shift)),
);
const assignedShifts = computed(
  () =>
    shiftsForJob.data.value &&
    shiftsForJob.data.value.filter(shift => isFulfilled(shift) || isCompleted(shift) || isNoShow(shift)),
);

watch([job.fetchStatus, job.isPending, job.isSuccess], async () => {
  if (job.isSuccess) {
    loading.value = false;
  }
});

watch([shortlisted.fetchStatus, shortlisted.isSuccess], async () => {
  if (shortlisted.isSuccess && shortlistedCandidates.value && shiftsForJob.isSuccess && assignedShifts.value) {
    if (assignedShifts.value.length > 0) {
      tabOpen.value = TABS.DECISION;
    } else if (shortlistedCandidates.value.length > 0) {
      tabOpen.value = TABS.SHORTLISTED;
    } else {
      tabOpen.value = TABS.APPLICANTS;
    }
  } else {
    tabOpen.value = TABS.APPLICANTS;
  }
});
</script>

<style scoped lang="scss">
main {
  width: 100%;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  overflow-y: scroll;
}

.job-details {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  padding: 1rem;
  gap: 1rem;
}

.loading-container {
  padding-top: 4rem;
}

.empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex-grow: 1;
  height: 100%;
  font-size: 13px;
  font-weight: 400;
  line-height: 18px;
  color: var(--palette-color-default-lighten-20);
  padding: 1.5rem;
  gap: 0.5rem;
  text-align: center;

  svg {
    fill: var(--palette-color-default-lighten-20);
  }
}

.accordions {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

/* Applicants */

.applicants {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  width: 100%;
}

.applicants .applicant-card {
  width: fit-content;

  .buttons {
    display: flex;
    justify-content: space-between;
    align-content: center;
    gap: 0.75rem;
    margin-top: 0.75rem;

    > button {
      width: 100%;
    }
  }
}

/* Shortlisted */

.shortlisted {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.shortlisted .add-interview-or-shift-title-wrapper {
  display: flex;
  align-items: center;

  .title {
    display: flex;
    flex-direction: column;
    flex: 1;
    color: var(--palette-color-default);
  }

  h2,
  p {
    margin-block: 0;
  }

  h2 {
    font-size: 18px;
    font-weight: 600;
    line-height: 26px;
    letter-spacing: -0.01em;
  }

  p {
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: -0.02em;
  }

  button {
    width: fit-content;
  }
}

.shortlisted .shortlisted-steps {
  display: grid;
  grid-template-columns: repeat(2, minmnax(0, 1fr));
  column-gap: 1.5rem;
}

.shortlisted .shortlisted-steps .group {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1rem;

  p {
    margin-block: 0;
  }

  .columns {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: 1rem;
  }
}

.list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.create-shift-cta {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 0.75rem;
  border: 1px solid var(--palette-color-default-lighten-80);
  border-radius: 12px;
  padding: 1rem;
  height: 100%;
  max-height: 400px;
  transition: all 0.1s ease-out;

  .texts {
    .title {
      font-size: 16px;
      font-weight: 600;
      transition: all 0.1s ease-out;
    }

    .description {
      font-size: 14px;
      font-weight: 400;
    }
  }

  &:hover {
    background-color: var(--palette-color-base-white);
    border-color: var(--palette-color-base-white);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);

    div span {
      color: var(--palette-color-brand-primary);
    }
  }

  &:active {
    opacity: 0.5;
    box-shadow: none;
    transition: all 0s;
  }
}

.hire-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: var(--palette-color-base-white);
  padding: 0.75rem 1rem 0.75rem 1.5rem;
  border: 0px solid var(--palette-color-default-lighten-90);
  border-top-width: 1px;
}

.hire-container-left {
  display: flex;
  flex-direction: column;
}

.hire-cost-span {
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  letter-spacing: -0.02em;
  text-align: left;
  color: var(--palette-color-default-lighten-30);
}

.hire-value-span {
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: -0.01em;
  text-align: left;
}

.hire-button {
  min-width: 15rem;
}

::v-deep .modal-content {
  padding: 1rem;
}

::v-deep .modal-footer {
  gap: 1rem;
  margin: 0.5rem 0;
}

.hire-content {
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  display: flex;
  flex-direction: column;

  > p {
    margin-block: 0;

    &:last-of-type {
      margin-top: 0.5rem;
    }
  }
}

.closed-container {
  display: flex;
  flex-direction: column;
  text-align: center;
  margin: 3rem 0;
}

.closed-info {
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: -0.01em;
}

.contact-details {
  margin-top: 1rem;
}

.contact-detail {
  display: flex;
  flex-direction: row;
  text-align: center;
  gap: 0.5rem;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  font-size: 14px;
}
</style>
